import { useState, useRef, useCallback, useEffect } from 'react';
import {
  fetchFoundingMembers,
  saveFoundingMembersToSupabase,
  addFoundingMember,
  updateFoundingMember,
  deleteFoundingMember,
  getLastSyncTime,
  updateLastSyncTime,
  type FoundingMember,
} from '@/utils/adminStore';
import ImageCropper from '@/components/base/ImageCropper';

interface FoundingForm {
  name: string;
  title: string;
  bio: string;
  tags: string;
  imagePrompt: string;
  imageUrl: string;
}

interface FoundingUI extends FoundingForm {
  id: number;
  sort_order: number;
}

const emptyForm: FoundingForm = {
  name: '',
  title: '',
  bio: '',
  tags: '',
  imagePrompt: '',
  imageUrl: '',
};

function toUI(m: FoundingMember): FoundingUI {
  return {
    id: m.id,
    sort_order: m.sort_order,
    name: m.name,
    title: m.title,
    bio: m.bio || '',
    tags: Array.isArray(m.tags) ? m.tags.join('、') : '',
    imagePrompt: m.image_prompt || '',
    imageUrl: m.image_url || '',
  };
}

function toRecord(m: FoundingUI): FoundingMember {
  return {
    id: m.id,
    sort_order: m.sort_order,
    name: m.name,
    title: m.title,
    bio: m.bio,
    tags: m.tags.split('、').map((t) => t.trim()).filter(Boolean),
    image_prompt: m.imagePrompt,
    image_url: m.imageUrl,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

export default function FoundingTeamManagement() {
  const [members, setMembers] = useState<FoundingUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FoundingForm>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(getLastSyncTime);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearSaveTimer = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = undefined;
    }
  };

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchFoundingMembers();
      setMembers(data.map(toUI));
    } catch {
      setLoadError('加载创始团队数据失败，请检查网络连接后刷新重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleSyncSave = async () => {
    clearSaveTimer();
    setSaveStatus('saving');
    try {
      const records = members.map(toRecord);
      const ok = await saveFoundingMembersToSupabase(records);
      if (ok) {
        const now = updateLastSyncTime();
        setLastSyncAt(now);
        setSaveStatus('saved');
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    }
    saveTimerRef.current = setTimeout(() => {
      setSaveStatus('idle');
    }, 2500);
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(members.map((m) => m.id)));
  const deselectAll = () => setSelected(new Set());

  const handleBatchDelete = async () => {
    const idsToDelete = [...selected];
    let successCount = 0;
    for (const id of idsToDelete) {
      const ok = await deleteFoundingMember(id);
      if (ok) successCount++;
    }
    if (successCount > 0) await loadMembers();
    setBatchDeleteConfirm(false);
    setSelected(new Set());
  };

  const isAllSelected = members.length > 0 && selected.size === members.length;
  const isSomeSelected = selected.size > 0;

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (m: FoundingUI) => {
    setEditingId(m.id);
    setForm({
      name: m.name,
      title: m.title,
      bio: m.bio,
      tags: m.tags,
      imagePrompt: m.imagePrompt,
      imageUrl: m.imageUrl,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.title.trim()) return;

    if (editingId !== null) {
      const ok = await updateFoundingMember(editingId, {
        name: form.name,
        title: form.title,
        bio: form.bio,
        tags: form.tags.split('、').map((t) => t.trim()).filter(Boolean),
        image_prompt: form.imagePrompt,
        image_url: form.imageUrl,
      });
      if (ok) await loadMembers();
    } else {
      const record = await addFoundingMember({
        name: form.name,
        title: form.title,
        bio: form.bio,
        tags: form.tags.split('、').map((t) => t.trim()).filter(Boolean),
        image_prompt: form.imagePrompt,
        image_url: form.imageUrl,
        sort_order: members.length,
      });
      if (record) await loadMembers();
    }
    setShowModal(false);
  };

  const handleDelete = async (id: number) => {
    const ok = await deleteFoundingMember(id);
    if (ok) await loadMembers();
    setDeleteConfirm(null);
  };

  const moveUp = async (index: number) => {
    if (index === 0) return;
    const updated = [...members];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    const reordered = updated.map((m, i) => ({ ...m, sort_order: i }));
    setMembers(reordered);
    const records = reordered.map(toRecord);
    await saveFoundingMembersToSupabase(records);
  };

  const moveDown = async (index: number) => {
    if (index === members.length - 1) return;
    const updated = [...members];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    const reordered = updated.map((m, i) => ({ ...m, sort_order: i }));
    setMembers(reordered);
    const records = reordered.map(toRecord);
    await saveFoundingMembersToSupabase(records);
  };

  const updateField = (field: keyof FoundingForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) return;

    setUploading(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setCropImageSrc(dataUrl);
      setUploading(false);
    };
    reader.onerror = () => setUploading(false);
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (croppedDataUrl: string) => {
    updateField('imageUrl', croppedDataUrl);
    setCropImageSrc(null);
  };

  const handleCropCancel = () => {
    setCropImageSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const triggerUpload = () => fileInputRef.current?.click();

  const removeImage = () => {
    updateField('imageUrl', '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-3xl text-brand-warm mb-3" />
        <p className="text-sm text-gray-400">正在加载创始团队数据...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 text-red-400 mb-4">
          <i className="ri-error-warning-line text-2xl" />
        </div>
        <p className="text-sm text-gray-500 mb-4">{loadError}</p>
        <button
          onClick={() => loadMembers()}
          className="px-4 py-2 text-sm font-medium bg-brand-deep text-white rounded-lg hover:bg-brand-dark transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-1" />重试
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-brand-deep">创始团队</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            创始团队成员数据（共 {members.length} 人）
            {isSomeSelected && <span className="ml-2 text-brand-brown font-medium">已选 {selected.size} 人</span>}
            {lastSyncAt && <span className="ml-2 text-gray-300">· 上次同步：{lastSyncAt}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saveStatus !== 'idle' && (
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              saveStatus === 'saving' ? 'bg-amber-50 text-amber-600' :
              saveStatus === 'saved' ? 'bg-green-50 text-green-600' :
              'bg-red-50 text-red-600'
            }`}>
              <i className={`${
                saveStatus === 'saving' ? 'ri-loader-4-line animate-spin' :
                saveStatus === 'saved' ? 'ri-check-line' :
                'ri-close-line'
              } mr-1`} />
              {saveStatus === 'saving' ? '同步中...' : saveStatus === 'saved' ? '已同步 ✓' : '同步失败'}
            </span>
          )}
          <button
            onClick={handleSyncSave}
            disabled={saveStatus === 'saving' || members.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-brand-brown text-white rounded-lg hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={`${saveStatus === 'saving' ? 'ri-loader-4-line animate-spin' : 'ri-cloud-line'}`} />
            保存到云端
          </button>
          {isSomeSelected && (
            <button
              onClick={() => setBatchDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line" />批量删除 ({selected.size})
            </button>
          )}
          {isSomeSelected ? (
            <button
              onClick={deselectAll}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-brand-cream text-gray-500 rounded-lg hover:bg-brand-light transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line" />取消选择
            </button>
          ) : (
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-brand-deep text-white rounded-lg hover:bg-brand-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />新增成员
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-brand-light/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-light/30 bg-brand-gray">
                <th className="text-center px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={() => isAllSelected ? deselectAll() : selectAll()}
                    className="w-4 h-4 rounded border-brand-cream text-brand-deep focus:ring-brand-warm cursor-pointer"
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 w-12">#</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 w-14">头像</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400">姓名</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400">职位</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 hidden lg:table-cell">标签</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 hidden xl:table-cell">简介</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 w-36">操作</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => (
                <tr key={m.id || i} className={`border-b border-brand-light/20 hover:bg-brand-gray/50 transition-colors ${selected.has(m.id) ? 'bg-brand-light/40' : ''}`}>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(m.id)}
                      onChange={() => toggleSelect(m.id)}
                      className="w-4 h-4 rounded border-brand-cream text-brand-deep focus:ring-brand-warm cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-xs">{i + 1}</td>
                  <td className="px-4 py-3">
                    {m.imageUrl ? (
                      <img src={m.imageUrl} alt={m.title} className="w-9 h-9 rounded-md object-contain border border-brand-light" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-brand-light flex items-center justify-center">
                        <i className="ri-user-line text-sm text-gray-300" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-brand-deep">{m.name}</td>
                  <td className="px-4 py-3 text-gray-500">{m.title || '-'}</td>
                  <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">{m.tags || '-'}</td>
                  <td className="px-4 py-3 text-gray-400 hidden xl:table-cell max-w-[200px] truncate">{m.bio}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => moveUp(i)} disabled={i === 0} className="w-7 h-7 flex items-center justify-center rounded-md text-gray-300 hover:text-brand-deep hover:bg-brand-light disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer" title="上移">
                        <i className="ri-arrow-up-s-line text-sm" />
                      </button>
                      <button onClick={() => moveDown(i)} disabled={i === members.length - 1} className="w-7 h-7 flex items-center justify-center rounded-md text-gray-300 hover:text-brand-deep hover:bg-brand-light disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer" title="下移">
                        <i className="ri-arrow-down-s-line text-sm" />
                      </button>
                      <button onClick={() => openEdit(m)} className="w-7 h-7 flex items-center justify-center rounded-md text-gray-300 hover:text-brand-deep hover:bg-brand-light transition-colors cursor-pointer" title="编辑">
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button onClick={() => setDeleteConfirm(m.id)} className="w-7 h-7 flex items-center justify-center rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title="删除">
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-300 text-sm">暂无成员数据，点击"新增成员"添加</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {cropImageSrc && (
        <ImageCropper
          image={cropImageSrc}
          aspect={3 / 4}
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-brand-light/30 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="text-base font-semibold text-brand-deep">
                {editingId !== null ? '编辑创始成员' : '新增创始成员'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-brand-deep hover:bg-brand-light transition-colors cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-brand-deep mb-2">头像照片</label>
                <div className="flex items-center gap-4">
                  <div
                    className="w-20 h-20 rounded-xl border-2 border-dashed border-brand-cream flex items-center justify-center overflow-hidden bg-brand-gray shrink-0 cursor-pointer hover:border-brand-warm transition-colors"
                    onClick={triggerUpload}
                  >
                    {form.imageUrl ? (
                      <img src={form.imageUrl} alt="预览" className="w-full h-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-gray-300">
                        {uploading ? (
                          <i className="ri-loader-4-line animate-spin text-lg" />
                        ) : (
                          <>
                            <i className="ri-camera-line text-lg" />
                            <span className="text-[10px]">上传</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <button type="button" onClick={triggerUpload} className="px-3 py-1.5 text-xs font-medium border border-brand-cream text-brand-deep rounded-lg hover:bg-brand-light transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-upload-2-line mr-1" />选择图片
                    </button>
                    {form.imageUrl && (
                      <button type="button" onClick={removeImage} className="px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-delete-bin-line mr-1" />移除图片
                      </button>
                    )}
                    <p className="text-[10px] text-gray-300">支持 JPG/PNG，不超过 5MB</p>
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} className="hidden" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-brand-deep mb-1">姓名 *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-brand-light bg-brand-gray text-brand-deep placeholder:text-gray-300 focus:outline-none focus:border-brand-warm transition-colors"
                  placeholder="例如：陈志远"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-brand-deep mb-1">职位 *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-brand-light bg-brand-gray text-brand-deep placeholder:text-gray-300 focus:outline-none focus:border-brand-warm transition-colors"
                  placeholder="例如：董事长 / 创始人"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-brand-deep mb-1">个人简介</label>
                <textarea
                  value={form.bio}
                  onChange={(e) => updateField('bio', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-brand-light bg-brand-gray text-brand-deep placeholder:text-gray-300 focus:outline-none focus:border-brand-warm transition-colors resize-none"
                  placeholder="例如：深耕健康管理行业二十余年..."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-brand-deep mb-1">标签</label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => updateField('tags', e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-brand-light bg-brand-gray text-brand-deep placeholder:text-gray-300 focus:outline-none focus:border-brand-warm transition-colors"
                  placeholder="多个标签用顿号分隔，例如：品牌战略、连锁经营、产业投资"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-brand-light/30 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-brand-deep hover:bg-brand-light rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || !form.title.trim()}
                className="px-5 py-2 text-sm font-medium bg-brand-deep text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {editingId !== null ? '保存修改' : '添加成员'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50 text-red-500">
                <i className="ri-error-warning-line text-xl" />
              </div>
              <h3 className="text-base font-semibold text-brand-deep mb-1">确认删除</h3>
              <p className="text-sm text-gray-400 mb-6">
                确定要删除成员「{members.find((m) => m.id === deleteConfirm)?.name}」吗？此操作不可撤销。
              </p>
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-400 hover:bg-brand-light rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                  取消
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">
                  确认删除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {batchDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setBatchDeleteConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50 text-red-500">
                <i className="ri-error-warning-line text-xl" />
              </div>
              <h3 className="text-base font-semibold text-brand-deep mb-1">批量删除确认</h3>
              <p className="text-sm text-gray-400 mb-6">确定要删除选中的 {selected.size} 位成员吗？此操作不可撤销。</p>
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => setBatchDeleteConfirm(false)} className="px-4 py-2 text-sm font-medium text-gray-400 hover:bg-brand-light rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                  取消
                </button>
                <button onClick={handleBatchDelete} className="px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">
                  确认删除 {selected.size} 位
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}